import {announce} from '@github-ui/aria-live'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useEffect} from 'react'

import styles from './ModelPicker.module.css'

export type AutoModelTier = 'efficiency' | 'balance' | 'intelligence'
export type AutoModelTierState = AutoModelTier | 'fast'

export interface AutoTierPickerConfig {
  effectiveTier: AutoModelTierState
  pendingTier?: AutoModelTierState
  error?: string
  onTierSelected: (tier: AutoModelTier) => void
}

export type VisibleAutoTierPickerConfig = AutoTierPickerConfig & {
  effectiveTier: AutoModelTier
  pendingTier?: AutoModelTier
}

export const AUTO_MODEL_TIERS = ['efficiency', 'balance', 'intelligence'] as const satisfies readonly AutoModelTier[]

const AUTO_TIER_DETAILS: Record<AutoModelTier, {label: string; description: string}> = {
  efficiency: {
    label: 'Efficiency',
    description: 'Prioritizes speed and efficiency',
  },
  balance: {
    label: 'Balance',
    description: 'Balances speed and intelligence',
  },
  intelligence: {
    label: 'Intelligence',
    description: 'Prioritizes intelligence',
  },
}

export function isVisibleAutoTierPickerConfig(
  config: AutoTierPickerConfig | undefined,
): config is VisibleAutoTierPickerConfig {
  return config !== undefined && config.effectiveTier !== 'fast' && config.pendingTier !== 'fast'
}

export function getAutoTierLabel(tier: AutoModelTier) {
  return AUTO_TIER_DETAILS[tier].label
}

export function getAutoTierModelDisplayName(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  return `${modelDisplayName} · ${getAutoTierLabel(config.effectiveTier)}`
}

export function getAutoTierModelAccessibleLabel(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  const displayName = getAutoTierModelDisplayName(modelDisplayName, config)
  const pendingLabel = config.pendingTier ? getAutoTierLabel(config.pendingTier) : undefined

  return pendingLabel ? `Model: ${displayName}, switching to ${pendingLabel}` : `Model: ${displayName}`
}

export function AutoTierAnnouncements({config}: {config: VisibleAutoTierPickerConfig}) {
  const pendingLabel = config.pendingTier ? getAutoTierLabel(config.pendingTier) : undefined

  useEffect(() => {
    const message = config.error ?? (pendingLabel ? `Switching to ${pendingLabel}…` : undefined)
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- controlled async status changes must be announced when their props update
    if (message) announce(message)
  }, [config.error, pendingLabel])

  return null
}

interface AutoTierMenuProps {
  config: VisibleAutoTierPickerConfig
  onBack: () => void
}

export function AutoTierMenu({config, onBack}: AutoTierMenuProps) {
  const pendingLabel = config.pendingTier ? getAutoTierLabel(config.pendingTier) : undefined

  return (
    <>
      <ActionList>
        <ActionList.Item
          onSelect={event => {
            event.preventDefault()
            onBack()
          }}
        >
          <ActionList.LeadingVisual>
            <ArrowLeftIcon />
          </ActionList.LeadingVisual>
          Back to models
        </ActionList.Item>
        <ActionList.Divider />
        <ActionList.Group selectionVariant="single">
          <ActionList.GroupHeading>Optimized for</ActionList.GroupHeading>
          {AUTO_MODEL_TIERS.map(tier => {
            const details = AUTO_TIER_DETAILS[tier]
            return (
              <ActionList.Item
                key={tier}
                selected={tier === config.effectiveTier}
                disabled={pendingLabel !== undefined}
                onSelect={() => config.onTierSelected(tier)}
              >
                {details.label}
                <ActionList.Description variant="block">{details.description}</ActionList.Description>
              </ActionList.Item>
            )
          })}
        </ActionList.Group>
      </ActionList>
      {pendingLabel || config.error ? (
        <div className={styles.autoTierMessages}>
          {pendingLabel ? <div className={styles.autoTierStatus}>Switching to {pendingLabel}…</div> : null}
          {config.error ? <div className={styles.autoTierError}>{config.error}</div> : null}
        </div>
      ) : null}
    </>
  )
}
